import { useState } from 'react';
import { Info, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { QuotationLineItemDto } from '../types/quotation.types';

interface LineItemInfoPopoverProps {
  item: QuotationLineItemDto;
  onRevert?: () => void;
  onDelete?: () => void;
}

export function LineItemInfoPopover({ item, onRevert, onDelete }: LineItemInfoPopoverProps) {
  const [open, setOpen] = useState(false);
  const hasMatched = !!item.matchedSalesDescription?.trim();
  const hasNotes = !!item.notes?.trim();
  const hasPossible = (item.possibleMatches?.length ?? 0) > 0;
  const canRevert = !!onRevert && !!item.isEdited;
  const canDelete = !!onDelete;

  const showPopover = hasMatched || hasNotes || hasPossible || canRevert || canDelete;
  if (!showPopover) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
          tabIndex={-1}
          // UI gate 5: an accessible name matching the hover label. A native title rather than a Radix
          // Tooltip, because this trigger renders on every row of the table and the review list
          // (UI-GUIDELINES §8: a tooltip root per row is the 239 ms lesson); it is out of the tab order
          // (tabIndex -1), so there is no keyboard focus for a tooltip to show on.
          title="Match details"
          aria-label="Match details"
        >
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96">
        <div className="space-y-3">
          {hasMatched && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Matched NetSuite item
              </p>
              <p className="mt-1 text-sm font-medium">{item.matchedSalesDescription}</p>
            </div>
          )}

          {hasNotes && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Notes
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{item.notes}</p>
            </div>
          )}

          {hasPossible && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {item.matchStatus === 'NO_MATCH' ? 'Why not matched?' : 'Possible alternatives'}
              </p>
              {item.matchStatus === 'NO_MATCH' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  The extracted length didn&apos;t match a stocked SKU exactly. These are the closest options.
                </p>
              )}
              <ul className="mt-2 space-y-1.5">
                {item.possibleMatches?.map((pm) => (
                  <li key={pm.itemId} className="rounded-md border bg-muted/30 p-2 text-xs">
                    <p className="font-medium text-foreground">{pm.salesDescription}</p>
                    <p className="mt-0.5 text-muted-foreground">
                      Length {pm.lengthM}m · {pm.weightPerPcKg.toFixed(2)} kg/pc · item #{pm.itemId}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(canRevert || canDelete) && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {canRevert && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onRevert!();
                    setOpen(false);
                  }}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Revert
                </Button>
              )}
              {canDelete && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    onDelete!();
                    setOpen(false);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
